import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import Loading from '../Common/Loading';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface TutorialData {
    lesson_name:  string;
    title_suffix: string;
    html_content: string;
    show_swf:     string;
    base_path:    string;
    lesson_code:  string;
    country:      string;
    mdcode:       string;
}

// ---------------------------------------------------------------------------
// NavButton — GIF hover pattern (_n / _o / _d)
// ---------------------------------------------------------------------------
const NavButton: React.FC<{
    name: string;
    onClick?: () => void;
    visible?: boolean;
}> = ({ name, onClick, visible = true }) => {
    const [suffix, setSuffix] = useState('n');
    if (!visible) return null;
    return (
        <img
            src={`/Image/${name}_${suffix}.gif`}
            alt={name}
            style={{ cursor: 'pointer', verticalAlign: 'middle' }}
            onMouseEnter={() => setSuffix('o')}
            onMouseLeave={() => setSuffix('n')}
            onMouseDown={() => setSuffix('d')}
            onMouseUp={() => setSuffix('o')}
            onClick={onClick}
            draggable={false}
        />
    );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
const Tutorial_PrincipleContents: React.FC = () => {
    const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;
    const [searchParams]  = useSearchParams();
    const iframeRef       = useRef<HTMLIFrameElement>(null);
    const audioPlayerRef  = useRef<HTMLAudioElement | null>(null);

    const [data,    setData]    = useState<TutorialData | null>(null);
    const [loading, setLoading] = useState(true);

    // ------------------------------------------------------------------
    // URL params  (mirrors VB Request.QueryString)
    // ------------------------------------------------------------------
    const country     = searchParams.get('Country')   || 'Australia';
    const lessonId    = searchParams.get('Lessonid')  || searchParams.get('Title') || searchParams.get('Lsncode') || '';
    const tp          = searchParams.get('tp')        || 'tut';
    const mdcode      = searchParams.get('mdcode')    || '';
    const searchWords = searchParams.get('SearchWords') || '';
    const matchType   = searchParams.get('match')     || '';

    // PGM-394: hide Back / Logout when opened via Title param (popup mode)
    const isPopupMode = searchParams.has('Title');

    // VB sets body background white
    document.body.style.backgroundColor = '#fff';

    // ------------------------------------------------------------------
    // Styles  (faithful to VB ASPX + Tutorial.css classes)
    // ------------------------------------------------------------------
    const styles: Record<string, React.CSSProperties> = {
        navigator: {
            display:         'flex',
            alignItems:      'center',
            justifyContent:  'space-between',
            backgroundColor: '#7a96df',
            height:          '50px',
            padding:         '0 10px',
            color:           'white',
            fontSize:        '18px',
            fontFamily:      'Arial',
        },
        navCenter: {
            textAlign: 'center',
            flexGrow:  1,
            fontSize:  '24px',
        },
        navLink: {
            color:          'white',
            textDecoration: 'none',
            fontWeight:     'bold',
            fontFamily:     'Arial',
        },
        // .HeadMsg equivalent — white background as per T3260 31/08/2023 patch
        titleCell: {
            backgroundColor: 'white',
            fontSize:        '18pt',
            fontFamily:      'Arial',
            width:           '100%',
            padding:         '0 0 0 10px',
        },
        titleSpan: {
            fontSize:   '16pt',
            color:      'maroon',
        },
        // Whiteboard link — hidden by default (VB: display:none)
        whiteboardLink: {
            fontSize:    '11pt',
            color:       'maroon',
            display:     'none',
            fontWeight:  'bold',
            marginLeft:  '104px',
        },
        iframeContainer: {
            WebkitOverflowScrolling: 'touch',
            overflow:                'auto',
        },
        iframe: {
            display:   'block',
            width:     '100%',
            border:    'none',
            minHeight: '600px',
        },
        outerTable: {
            fontFamily: 'Arial',
            width:      '100%',
            zIndex:     100,
            borderCollapse: 'collapse',
        },
    };

    // ------------------------------------------------------------------
    // Audio helper — mirrors window.playSound in Tutorial_PrinicipleContent.js
    // ------------------------------------------------------------------
    const playSound = (path: string) => {
        if (!path || !data) return;
        if (audioPlayerRef.current) {
            audioPlayerRef.current.pause();
            audioPlayerRef.current.currentTime = 0;
        }
        const fullPath = data.base_path + path;
        audioPlayerRef.current = new Audio(fullPath);
        audioPlayerRef.current.play().catch(err =>
            console.log('Audio playback failed:', err)
        );
    };

    // ------------------------------------------------------------------
    // Fetch  (mirrors VB Page_Load + get_tutorial_content Django view)
    // ------------------------------------------------------------------
    useEffect(() => {
        const fetchContent = async () => {
            if (!lessonId) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const url = `${API_BASE_URL}api/get_tutorial_content?lesson_id=${lessonId}&country=${country}&tp=${tp}&mdcode=${mdcode}`;
                const res  = await fetch(url);
                const json = await res.json();
                if (json.error) {
                    alert(json.error);
                } else {
                    setData(json);
                }
            } catch (err) {
                console.error('Fetch Error:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchContent();
    }, [lessonId, country, tp, mdcode]); // eslint-disable-line react-hooks/exhaustive-deps

    // ------------------------------------------------------------------
    // iframe onLoad — full port of Tutorial_PrinicipleContent.js
    // ------------------------------------------------------------------
    const handleIframeLoad = () => {
        if (!iframeRef.current || !data) return;

        const iframe    = iframeRef.current;
        const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
        if (!iframeDoc) return;

        // ---- Bridge: inject playSound into the iframe's window so that
        //      any inline onclick="playSound(…)" calls in the legacy HTML
        //      are routed to the parent's audio player (PGM-406).
        if (iframe.contentWindow) {
            (iframe.contentWindow as any).playSound = (_id: string, path: string) => {
                playSound(path);
            };
        }

        // ----------------------------------------------------------------
        // 1. Process all divs starting with "divShow"
        //    (mirrors the divs.forEach block in legacy JS exactly)
        // ----------------------------------------------------------------
        const divs = iframeDoc.querySelectorAll<HTMLElement>('[id^="divShow"]');

        divs.forEach((div, index) => {
            // Un-hide
            div.style.display = 'block';

            // PGM-431: strip background colours from TABLESTEP tables + cells
            // Uses Array.from(div.children).filter() — NOT querySelectorAll —
            // to match the exact legacy JS behaviour (direct children only).
            const tables = Array.from(div.children).filter(
                el =>
                    el.tagName === 'TABLE' &&
                    el.getAttribute('name') &&
                    el.getAttribute('name')!.toUpperCase().includes('TABLESTEP')
            ) as HTMLTableElement[];

            tables.forEach(t => {
                const newStyle = (t.getAttribute('style') || '')
                    .replace(/background(-color)?\s*:\s*[^;]+;?/gi, '')
                    .trim();
                t.setAttribute('style', newStyle);

                const tds = Array.from(t.rows).flatMap(r => Array.from(r.cells));
                tds.forEach(td => {
                    const tdNew = (td.getAttribute('style') || '')
                        .replace(/background(-color)?\s*:\s*[^;]+;?/gi, '')
                        .trim();
                    td.setAttribute('style', tdNew);
                });
            });

            // Remove <center> tags (unwrap children, remove element)
            const centers = div.querySelectorAll('center');
            centers.forEach(center => {
                while (center.firstChild) {
                    center.parentNode?.insertBefore(center.firstChild, center);
                }
                center.remove();
            });

            // Add "Page N" label
            const pageLabel = iframeDoc.createElement('div');
            pageLabel.textContent    = `Page ${index + 1}`;
            pageLabel.style.fontWeight   = 'bold';
            pageLabel.style.fontSize     = '18px';
            pageLabel.style.marginBottom = '10px';
            pageLabel.style.textAlign    = 'left';
            pageLabel.style.color        = '#333';
            div.insertBefore(pageLabel, div.firstChild);

            // ---- SWF detection + HTML5 fallback container ---------------
            // (mirrors the swfObjects block in legacy JS)
            const swfObjects = div.querySelectorAll<Element>(
                'object[data$=".swf"], embed[src$=".swf"]'
            );
            swfObjects.forEach(obj => {
                const swfPath = obj.getAttribute('data') || obj.getAttribute('src');
                obj.remove();

                const container = iframeDoc.createElement('div');
                container.style.border     = '1px solid #aaa';
                container.style.padding    = '10px';
                container.style.margin     = '15px 0';
                container.style.background = '#f9f9f9';
                container.style.textAlign  = 'center';

                if (swfPath) {
                    container.innerHTML = `
                        <div style="margin-bottom:8px;font-size:14px;color:#555;">
                            SWF Animation: <span style="font-weight:bold;">${swfPath}</span>
                        </div>
                        <object width="100%" height="400" data="${swfPath}" type="application/x-shockwave-flash">
                            <param name="movie" value="${swfPath}">
                            <param name="quality" value="high">
                            <param name="allowFullScreen" value="true">
                            <param name="allowScriptAccess" value="always">
                            <p>Flash content is not supported in this browser.</p>
                        </object>`;
                } else {
                    container.textContent = 'No SWF animation found for this page.';
                }

                div.appendChild(container);
            });

            // ---- Audio click handler on TABLE_Pn_x steps ----------------
            // Matches legacy: step.title.includes("=") → split("=")[1]
            const steps = div.querySelectorAll<HTMLElement>(
                `table[id^='TABLE_P${index + 1}_']`
            );
            steps.forEach(step => {
                if (step.title && step.title.includes('=')) {
                    const audioPath = step.title.split('=')[1];
                    step.style.cursor = 'pointer';
                    step.addEventListener('click', () => playSound(audioPath));
                }
            });
        });

        // ----------------------------------------------------------------
        // 2. Adjust iframe height (legacy JS uses scrollHeight + "px",
        //    i.e. no +50 offset — matches original exactly)
        // ----------------------------------------------------------------
        setTimeout(() => {
            if (iframeRef.current && iframeDoc.body) {
                iframeRef.current.style.height =
                    iframeDoc.body.scrollHeight + 'px';
            }
        }, 300);

        // ----------------------------------------------------------------
        // 3. FrameObj iframe handling
        //    Mirrors the $frameObjs.each block in legacy JS exactly,
        //    including showSWF / lsnType / mobile-UA branching.
        // ----------------------------------------------------------------
        const frameObjs = iframeDoc.querySelectorAll<HTMLIFrameElement>(
            "iframe[id^='FrameObj']"
        );
        if (frameObjs.length > 0) {
            const eqCountry  = data.country;
            const lsnCode    = data.lesson_code;
            const showSWF    = data.show_swf;
            // lsnType would come from hidType in VB; not yet in API response —
            // add 'lesson_type' field to the Django view if Construct2 lessons exist.
            const lsnType    = (data as any).lesson_type || '';
            const isMobile   = /(android|iphone|ipad|ipod)/i.test(navigator.userAgent);

            frameObjs.forEach(frame => {
                const frameId = frame.getAttribute('id') || '';
                let srcPath   = '';

                if (showSWF === 'N') {
                    // HTML5 version
                    srcPath = `/Storage/eldata/html5/Tutorials/${eqCountry}/${lsnCode}_E/${lsnCode}_Tut_${frameId}.html`;
                } else if (showSWF === 'Y') {
                    if (lsnType === 'Construct2') {
                        srcPath = `/Storage/eldata/html5/Construct2/${eqCountry}/${lsnCode}/${lsnCode}_Tut_${frameId}.html`;
                    } else if (isMobile) {
                        srcPath = `/Storage/eldata/html5/Tutorials/${eqCountry}/${lsnCode}_E/${lsnCode}_Tut_${frameId}.html`;
                    } else {
                        srcPath = `/Storage/eldata/DicFiles/${eqCountry}/${lsnCode}_E/${lsnCode}_Tut_${frameId}.htm`;
                    }
                }

                if (srcPath) {
                    frame.setAttribute('src', srcPath);
                }
            });
        }

        // ----------------------------------------------------------------
        // 4. Search word highlighting
        //    Full port of highlightFromQuery() in legacy JS
        // ----------------------------------------------------------------
        if (searchWords && iframeDoc.body) {
            const word = decodeURIComponent(searchWords).trim();
            if (word) {
                let regex: RegExp;
                if (/\\b/.test(word)) {
                    regex = new RegExp(word, 'gi');
                } else {
                    const safeWord = word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                    regex =
                        matchType === 'Exact match'
                            ? new RegExp(`\\b${safeWord}\\b`, 'gi')
                            : new RegExp(safeWord, 'gi');
                }

                const walker = iframeDoc.createTreeWalker(
                    iframeDoc.body,
                    NodeFilter.SHOW_TEXT,
                    {
                        acceptNode(node) {
                            if (!node.nodeValue?.trim()) return NodeFilter.FILTER_REJECT;
                            const tag = node.parentNode?.nodeName;
                            if (tag === 'SCRIPT' || tag === 'STYLE') return NodeFilter.FILTER_REJECT;
                            return NodeFilter.FILTER_ACCEPT;
                        },
                    }
                );

                const nodes: Node[] = [];
                let n: Node | null;
                while ((n = walker.nextNode())) {
                    regex.lastIndex = 0;
                    if (regex.test(n.nodeValue || '')) nodes.push(n);
                }

                nodes.forEach(node => {
                    const span = iframeDoc.createElement('span');
                    span.innerHTML = (node.nodeValue || '').replace(
                        regex,
                        m =>
                            `<span style="background-color:yellow;color:black;padding:2px;border-radius:3px;">${m}</span>`
                    );
                    node.parentNode?.replaceChild(span, node);
                });
            }
        }
    };

    // ------------------------------------------------------------------
    // Back button handler  (mirrors imgbtnBack_Click in VB)
    // ------------------------------------------------------------------
    const handleBack = () => {
        const url =
            `/LessonBrowser/LessonViewer?Lessonid=${lessonId}&Country=${country}` +
            (mdcode ? `&mdcode=${mdcode}` : '');
        window.location.href = url;
    };

    // ------------------------------------------------------------------
    // Render — faithful to the ASPX table structure + CSS classes
    // ------------------------------------------------------------------
    return (
        <div style={{ margin: 0, padding: 0, fontFamily: 'Arial, sans-serif', overflowX: 'hidden' }}>
            {loading && <Loading />}

            {/* ---- Lesson Navigator bar (mirrors #Lessonnavigator) ---- */}
            <div id="Lessonnavigator" style={styles.navigator}>
                {!isPopupMode ? (
                    <NavButton name="back" onClick={handleBack} />
                ) : (
                    <div style={{ width: '50px' }} />
                )}

                <div className="center" style={styles.navCenter}>
                    Lesson Navigator
                </div>

                {!isPopupMode ? (
                    <a href="/" style={styles.navLink}>Logout</a>
                ) : (
                    <div style={{ width: '50px' }} />
                )}
            </div>

            {/* ---- Outer table — mirrors VB table with TutHeader row ---- */}
            <table
                cellPadding={0}
                cellSpacing={0}
                style={styles.outerTable}
            >
                {/* Title row — .TutHeader / .HeadMsg */}
                <tbody>
                    <tr className="TutHeader">
                        <td
                            id="tdTitle"
                            className="HeadMsg"
                            style={styles.titleCell}
                        >
                            {/* spanTitle — mirrors VB spanTitle.InnerText */}
                            <span
                                id="spanTitle"
                                style={styles.titleSpan}
                            >
                                {data
                                    ? `${data.lesson_name} (${lessonId}) - ${data.title_suffix}`
                                    : ''}
                            </span>
                            &nbsp;&nbsp;
                            {/* spanCurrentPageNo / spanNoOfTutorialPages
                                These are managed by legacy JS reading divShow count;
                                page label is injected per-div in handleIframeLoad instead */}
                            <span id="spanCurrentPageNo"  style={{ fontSize: '11pt', color: 'maroon', fontWeight: 'bold' }} />
                            <span id="spanNoOfTutorialPages" style={{ fontSize: '11pt', color: 'maroon', fontWeight: 'bold' }} />
                            <br />
                            <span style={{ marginLeft: '104px' }}>
                                {/* Whiteboard link — hidden by default, VB shows conditionally */}
                                <a
                                    id="WhiteBoardLink"
                                    style={styles.whiteboardLink}
                                    target="_blank"
                                    rel="noreferrer"
                                    href="#"
                                >
                                    Whiteboard
                                </a>
                            </span>
                        </td>
                    </tr>

                    {/* Content row — mirrors tdFrameTut */}
                    <tr>
                        <td
                            id="tdFrameTut"
                            valign="top"
                            style={{ display: 'block', padding: 0 }}
                        >
                            <div
                                id="divFrameTut"
                                style={styles.iframeContainer}
                            >
                                {data && data.html_content ? (
                                    <iframe
                                        ref={iframeRef}
                                        id="iframeTut"
                                        style={styles.iframe}
                                        srcDoc={data.html_content}
                                        onLoad={handleIframeLoad}
                                        title="Tutorial Content"
                                        scrolling="yes"
                                        frameBorder={0}
                                    />
                                ) : (
                                    !loading && (
                                        <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>
                                            Content file not found for this lesson.
                                        </div>
                                    )
                                )}
                            </div>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default Tutorial_PrincipleContents;